// Missed-dose backfill review modal (MISSED_DOSE_BACKFILL_PLAN.md Task 3). Lets the
// user mark each gap from the past 7 days "Took it" (default time = the scheduled
// anchor, editable) or "Didn't take it" (fixed at the anchor). Direct save, no
// pending/confirm, no Bedrock — POST /medications/backfill/log.
//
// Doses are grouped per day: by regimen when a slot belongs to one (e.g. "Morning
// Pills"), otherwise by exact expected clock time (so unrelated meds due at the same
// moment still collapse into one row instead of repeating "Expected TIME"). Group keys
// include the day, so each day's "Bedtime" is its own independent group.
//
// "Tap the exceptions": each medication is a chip. "Took all" / "Didn't take any" are
// one-click bulk actions; tapping a chip in an untouched group means "I missed this
// one" (the rest of the group stages as taken), and after that a tap flips the chip
// between taken and not taken. Undo and "Change time" act on that group only.
//
// "+ Add another medication": each group also offers a picker for a saved medication that
// was NOT on the list (e.g. a pill taken in place of one that was skipped). It is staged
// as taken at the group's taken time (so "Change time" moves it too) and saved in the same
// batch as an ordinary entry — the log endpoint rebuilds the payload from regimenId/lineId,
// so it needs no slot. Tapping an added chip removes it (it has no "not taken" state).

import { useState, type ReactNode } from "react";
import type {
  BackfillSlot,
  BackfillLogEntry,
  FeedbackRating,
  FeedbackCategory,
  MissedDoseBackfillFeedbackContext,
} from "@medassist/shared";
import { formatCalendarDate, FEEDBACK_CATEGORIES } from "@medassist/shared";
import { logBackfillEntries, submitFeedback, ApiError } from "../lib/api.js";
import { ModalShell, CHOICE_CLASS } from "./ModalShell.js";
import { MedPickerModal } from "./MedPickerModal.js";
import type { MedChoice } from "./medPicker.js";
import { PillIcon, ThumbUpIcon, ThumbDownIcon } from "./icons.js";
import { ClearableTextarea } from "./ClearableInput.js";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function localTimeStr(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function todayStr(): string {
  return localDateStr(new Date());
}

function daysAgoStr(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localDateStr(d);
}

function to12Hour(hm: string): string {
  const [hStr, mStr] = hm.split(":");
  let h = Number(hStr);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mStr} ${ampm}`;
}

function isoTo12Hour(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function dayHeading(dayKey: string): string {
  if (dayKey === todayStr()) return "Today";
  if (dayKey === daysAgoStr(1)) return "Yesterday";
  return formatCalendarDate(dayKey, { weekday: "short", month: "short", day: "numeric" });
}

function dateTimeToIso(date: string, time: string): string {
  const [year, month, day] = date.split("-").map(Number);
  const [h, m] = time.split(":").map(Number);
  return new Date(year!, month! - 1, day!, h ?? 0, m ?? 0, 0, 0).toISOString();
}

interface Staged {
  missed: boolean;
  takenAt: string; // ISO
}

interface SlotGroup {
  key: string;
  regimenName?: string;
  slots: BackfillSlot[];
}

/** A saved medication the user added to a group that wasn't on the missed list. */
interface ExtraDose {
  /** Sent as the entry's slotId (unique per group + medication); the server only echoes it. */
  id: string;
  groupKey: string;
  choice: MedChoice;
  takenAt: string; // ISO
}

function medKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Buckets one day's slots: by regimen when present, else by exact expected clock time.
 * The key is day-qualified so the same regimen on two days never shares UI state.
 */
function groupDaySlots(dayKey: string, daySlots: BackfillSlot[]): SlotGroup[] {
  const byKey = new Map<string, BackfillSlot[]>();
  const order: string[] = [];
  for (const s of daySlots) {
    const key = s.regimenId ? `${dayKey}|r:${s.regimenId}` : `${dayKey}|t:${s.anchorLocalTime}`;
    const arr = byKey.get(key);
    if (arr) arr.push(s);
    else {
      byKey.set(key, [s]);
      order.push(key);
    }
  }
  return order.map((key) => {
    const slots = byKey.get(key)!;
    return { key, regimenName: slots[0]!.regimenId ? slots[0]!.regimenName : undefined, slots };
  });
}

/** Distinct values, formatted and joined — collapses to one value when uniform. */
function joinDistinct<T>(values: T[], format: (v: T) => string): string {
  return [...new Set(values)].map(format).join(", ");
}

const EDIT_UNDO_CLASS =
  "rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors";

export function MissedDoseBackfill({
  patientId,
  slots,
  onClose,
  onLogged,
}: {
  patientId: string;
  slots: BackfillSlot[];
  onClose: () => void;
  /** Called after at least one entry saves so the widget can refetch + toast. */
  onLogged: (count: number) => void;
}) {
  const [staged, setStaged] = useState<Map<string, Staged>>(new Map());
  const [editingGroupKey, setEditingGroupKey] = useState<string | null>(null);
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [settled, setSettled] = useState<Set<string>>(new Set()); // slotIds saved or skipped
  // name travels with the reason because a skipped added medication has no slot to look up.
  const [skipReasons, setSkipReasons] = useState<Map<string, { name: string; reason: string }>>(new Map());
  const [extras, setExtras] = useState<ExtraDose[]>([]);
  const [pickerGroup, setPickerGroup] = useState<SlotGroup | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Review feedback (mirrors ChatPane.tsx's handleFeedback* shape).
  const [feedbackRating, setFeedbackRating] = useState<FeedbackRating | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState<FeedbackCategory | "">("");
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const feedbackSessionId = `missed-dose-backfill-${todayStr()}`;

  // Carries the exact on-screen list + in-progress decisions so a rating isn't
  // context-free (mirrors SuggestionCard.tsx's pendingProposal/sourceSurface pattern).
  function backfillFeedbackContext(): MissedDoseBackfillFeedbackContext {
    return {
      kind: "missed_dose_backfill",
      slots,
      staged: [...staged.entries()].map(([slotId, s]) => ({ slotId, missed: s.missed, takenAt: s.takenAt })),
      added: extras.map((x) => ({
        name: x.choice.name,
        regimenId: x.choice.regimenId,
        lineId: x.choice.lineId,
        takenAt: x.takenAt,
      })),
    };
  }

  async function handleFeedbackThumb(rating: FeedbackRating) {
    if (rating === "up") {
      setFeedbackBusy(true);
      setFeedbackError(null);
      try {
        await submitFeedback({
          patientId,
          sessionId: feedbackSessionId,
          mode: "capture",
          rating: "up",
          pendingProposal: backfillFeedbackContext(),
          sourceSurface: "missed_dose_backfill",
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        setFeedbackRating("up");
        setFeedbackOpen(false);
      } catch (err) {
        setFeedbackError(err instanceof ApiError ? err.message : "Couldn't save feedback.");
      } finally {
        setFeedbackBusy(false);
      }
    } else {
      setFeedbackRating("down");
      setFeedbackOpen(true);
    }
  }

  async function handleFeedbackSubmitDown() {
    if (!feedbackCategory || !feedbackText.trim()) return;
    setFeedbackBusy(true);
    setFeedbackError(null);
    try {
      await submitFeedback({
        patientId,
        sessionId: feedbackSessionId,
        mode: "capture",
        rating: "down",
        reasonCategory: feedbackCategory as FeedbackCategory,
        reasonText: feedbackText.trim(),
        pendingProposal: backfillFeedbackContext(),
        sourceSurface: "missed_dose_backfill",
        // Must match the tz the on-screen list was computed in (GET
        // /medications/backfill takes the browser tz), or the server-side
        // backfillDebug recompute buckets days differently than the user saw.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setFeedbackOpen(false);
    } catch (err) {
      setFeedbackError(err instanceof ApiError ? err.message : "Couldn't save feedback.");
    } finally {
      setFeedbackBusy(false);
    }
  }

  const visible = slots.filter((s) => !settled.has(s.slotId));

  const byDay = new Map<string, BackfillSlot[]>();
  for (const s of visible) {
    const arr = byDay.get(s.dayKey);
    if (arr) arr.push(s);
    else byDay.set(s.dayKey, [s]);
  }

  function stageGroupTook(groupSlots: BackfillSlot[]) {
    setStaged((prev) => {
      const next = new Map(prev);
      for (const s of groupSlots) {
        // Keep an already-edited taken time; a fresh or missed slot takes its anchor.
        const existing = prev.get(s.slotId);
        next.set(s.slotId, { missed: false, takenAt: existing && !existing.missed ? existing.takenAt : s.anchorIso });
      }
      return next;
    });
  }

  function stageGroupMissed(groupSlots: BackfillSlot[]) {
    setStaged((prev) => {
      const next = new Map(prev);
      for (const s of groupSlots) next.set(s.slotId, { missed: true, takenAt: s.anchorIso });
      return next;
    });
    setEditingGroupKey(null);
  }

  function unstageGroup(group: SlotGroup) {
    setStaged((prev) => {
      const next = new Map(prev);
      for (const s of group.slots) next.delete(s.slotId);
      return next;
    });
    // Undo returns the whole group to untouched, added medications included.
    setExtras((prev) => prev.filter((x) => x.groupKey !== group.key));
    if (editingGroupKey === group.key) setEditingGroupKey(null);
  }

  /** The time a dose added to this group should carry: whatever the group's taken doses
   *  already use (so a "Change time" edit applies), else its first added dose, else the
   *  expected time. */
  function groupTakenAt(group: SlotGroup): string {
    for (const s of group.slots) {
      const a = staged.get(s.slotId);
      if (a && !a.missed) return a.takenAt;
    }
    return extras.find((x) => x.groupKey === group.key)?.takenAt ?? group.slots[0]!.anchorIso;
  }

  function addExtras(group: SlotGroup, meds: MedChoice[]) {
    const takenAt = groupTakenAt(group);
    setExtras((prev) => {
      const have = new Set(prev.filter((x) => x.groupKey === group.key).map((x) => medKey(x.choice.name)));
      const fresh = meds
        .filter((m) => !have.has(medKey(m.name)))
        .map((choice) => ({
          id: `extra|${group.key}|${choice.regimenId}:${choice.lineId}`,
          groupKey: group.key,
          choice,
          takenAt,
        }));
      return [...prev, ...fresh];
    });
  }

  function removeExtra(id: string) {
    setExtras((prev) => prev.filter((x) => x.id !== id));
  }

  /**
   * A chip tap. In an untouched group it means "I missed this one": every other dose in
   * the group stages as taken. Otherwise it flips this one dose between taken and missed.
   */
  function tapChip(group: SlotGroup, slot: BackfillSlot) {
    setStaged((prev) => {
      const next = new Map(prev);
      const untouched = group.slots.every((s) => !prev.has(s.slotId));
      if (untouched) {
        for (const s of group.slots) next.set(s.slotId, { missed: s.slotId === slot.slotId, takenAt: s.anchorIso });
        return next;
      }
      const current = prev.get(slot.slotId);
      if (current && !current.missed) {
        next.set(slot.slotId, { missed: true, takenAt: slot.anchorIso });
      } else {
        // Flipping back to taken reuses the group's taken time if "Change time" set one.
        const sibling = group.slots.map((s) => prev.get(s.slotId)).find((a) => a && !a.missed);
        next.set(slot.slotId, { missed: false, takenAt: sibling?.takenAt ?? slot.anchorIso });
      }
      return next;
    });
  }

  function openEdit(group: SlotGroup) {
    const d = new Date(groupTakenAt(group));
    setEditDate(localDateStr(d));
    setEditTime(localTimeStr(d));
    setEditingGroupKey(group.key);
  }

  /** Applies the edited date/time to every dose in the group currently staged as taken. */
  function applyEdit(group: SlotGroup) {
    if (!editDate || !editTime) return;
    const takenAt = dateTimeToIso(editDate, editTime);
    setStaged((prev) => {
      const next = new Map(prev);
      for (const s of group.slots) {
        const existing = prev.get(s.slotId);
        if (existing && !existing.missed) next.set(s.slotId, { ...existing, takenAt });
      }
      return next;
    });
    setExtras((prev) => prev.map((x) => (x.groupKey === group.key ? { ...x, takenAt } : x)));
    setEditingGroupKey(null);
  }

  async function save() {
    if (staged.size + extras.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const slotEntries: BackfillLogEntry[] = [...staged.entries()].map(([slotId, action]) => {
        const slot = slots.find((s) => s.slotId === slotId)!;
        return {
          slotId,
          regimenId: slot.regimenId,
          lineId: slot.lineId,
          name: slot.name,
          takenAt: action.takenAt,
          missed: action.missed,
        };
      });
      // An added medication has no slot; the server resolves it from regimenId/lineId.
      const extraEntries: BackfillLogEntry[] = extras.map((x) => ({
        slotId: x.id,
        regimenId: x.choice.regimenId,
        lineId: x.choice.lineId,
        name: x.choice.name,
        takenAt: x.takenAt,
        missed: false,
      }));
      const entries = [...slotEntries, ...extraEntries];
      const nameById = new Map(entries.map((e) => [e.slotId, e.name]));
      const { saved, skipped } = await logBackfillEntries(patientId, entries);
      const nowSettled = new Set(settled);
      const reasons = new Map(skipReasons);
      for (const s of saved) nowSettled.add(s.slotId);
      for (const s of skipped) {
        nowSettled.add(s.slotId);
        reasons.set(s.slotId, { name: nameById.get(s.slotId) ?? "Entry", reason: s.reason });
      }
      setSettled(nowSettled);
      setSkipReasons(reasons);
      setStaged(new Map());
      setExtras([]);
      if (saved.length > 0) onLogged(saved.length);
      if (skipped.length === 0) {
        onClose();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save");
    } finally {
      setBusy(false);
    }
  }

  const nothingLeft = visible.length === 0 && skipReasons.size === 0;

  /** One medication as a tappable chip: blank, taken (green check), or not taken (red cross). */
  function renderChip(group: SlotGroup, slot: BackfillSlot) {
    const action = staged.get(slot.slotId);
    const state = !action ? "none" : action.missed ? "missed" : "taken";
    const stateLabel =
      state === "taken"
        ? "taken, tap to mark not taken"
        : state === "missed"
          ? "not taken, tap to mark taken"
          : "tap if you missed it";
    return (
      <button
        key={slot.slotId}
        onClick={() => tapChip(group, slot)}
        aria-label={`${slot.name}: ${stateLabel}`}
        className={
          "inline-flex items-center gap-1.5 rounded-full border-[1.5px] py-1 pl-2 pr-3 text-sm font-medium transition-colors " +
          (state === "taken"
            ? "border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/50 dark:text-green-400"
            : state === "missed"
              ? "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-400"
              : "border-gray-300 text-gray-900 hover:border-(--accent) dark:border-gray-700 dark:text-gray-100")
        }
      >
        <span
          aria-hidden
          className={
            "grid h-4 w-4 place-items-center rounded-full border-[1.5px] text-[10px] font-bold leading-none " +
            (state === "taken"
              ? "border-green-600 bg-green-600 text-white dark:border-green-500 dark:bg-green-500"
              : state === "missed"
                ? "border-red-600 bg-red-600 text-white dark:border-red-500 dark:bg-red-500"
                : "border-gray-300 dark:border-gray-600")
          }
        >
          {state === "taken" ? "✓" : state === "missed" ? "✕" : ""}
        </span>
        <span className={state === "missed" ? "line-through" : ""}>{slot.name}</span>
        {slot.strength && <span className="text-xs font-normal opacity-75">{slot.strength}</span>}
      </button>
    );
  }

  /** A medication added to the group: always taken (green); tapping removes it. */
  function renderExtraChip(extra: ExtraDose) {
    const { choice } = extra;
    return (
      <button
        key={extra.id}
        onClick={() => removeExtra(extra.id)}
        aria-label={`${choice.name}: added, tap to remove`}
        title="Added — tap to remove"
        className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-green-300 bg-green-50 py-1 pl-2 pr-3 text-sm font-medium text-green-700 transition-colors dark:border-green-800 dark:bg-green-950/50 dark:text-green-400"
      >
        <span
          aria-hidden
          className="grid h-4 w-4 place-items-center rounded-full border-[1.5px] border-green-600 bg-green-600 text-[10px] font-bold leading-none text-white dark:border-green-500 dark:bg-green-500"
        >
          ✓
        </span>
        <span>{choice.name}</span>
        {choice.strength && <span className="text-xs font-normal opacity-75">{choice.strength}</span>}
        <span className="text-xs font-normal opacity-75">(added)</span>
      </button>
    );
  }

  function renderGroup(group: SlotGroup) {
    const { key, regimenName, slots: groupSlots } = group;
    const total = groupSlots.length;
    const takenTimes: string[] = [];
    let missedCount = 0;
    for (const s of groupSlots) {
      const a = staged.get(s.slotId);
      if (!a) continue;
      if (a.missed) missedCount++;
      else takenTimes.push(a.takenAt);
    }
    const takenCount = takenTimes.length;
    // Added medications are separate from the slot counts below: they are always taken and
    // never make the group "complete", but they do count toward the time shown / editable.
    const groupExtras = extras.filter((x) => x.groupKey === key);
    const extraTimes = groupExtras.map((x) => x.takenAt);
    const anyTakenTime = takenCount + groupExtras.length > 0;
    const untouched = takenCount + missedCount === 0;
    const complete = takenCount + missedCount === total;
    const isEditing = editingGroupKey === key;

    const borderClass = !complete
      ? "border-gray-200 dark:border-gray-800"
      : takenCount === total
        ? "border-green-300 dark:border-green-800"
        : missedCount === total
          ? "border-red-300 dark:border-red-800"
          : "border-gray-300 dark:border-gray-700";

    let badge: ReactNode = null;
    if (!untouched) {
      badge =
        takenCount === total ? (
          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-950/60 dark:text-green-400">
            {total === 1 ? "Taken" : `All ${total} taken`}
          </span>
        ) : missedCount === total ? (
          <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-950/60 dark:text-red-400">
            {total === 1 ? "Not taken" : "None taken"}
          </span>
        ) : (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300">
            {takenCount} of {total} taken
          </span>
        );
    }

    let hint: string;
    if (!untouched) hint = "Tap a medication to flip it.";
    else if (total > 1) hint = "Took them all? Tap Took all. Missed one? Just tap it.";
    else hint = "Tap the medication if you missed it, or choose below.";

    const changeTimeButton = anyTakenTime ? (
      <button
        onClick={() => openEdit(group)}
        className={`${EDIT_UNDO_CLASS} text-(--accent) hover:bg-(--accent)/10`}
      >
        Change time
      </button>
    ) : null;

    return (
      <li key={key} className={`rounded-lg border px-3 py-2 transition-colors ${borderClass}`}>
        {(regimenName || badge || groupExtras.length > 0) && (
          <div className="mb-1 flex items-center gap-2">
            <p className="flex-1 text-xs font-semibold uppercase tracking-wide text-(--accent)">{regimenName}</p>
            {badge}
            {groupExtras.length > 0 && (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-950/60 dark:text-green-400">
                +{groupExtras.length} added
              </span>
            )}
          </div>
        )}
        <p className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
        <div className="mb-2 flex flex-wrap gap-1.5">
          {groupSlots.map((s) => renderChip(group, s))}
          {groupExtras.map((x) => renderExtraChip(x))}
          <button
            onClick={() => setPickerGroup(group)}
            title="Add a medication you took at this time that isn't listed, e.g. a substitute"
            className="inline-flex items-center gap-1 rounded-full border-[1.5px] border-dashed border-gray-300 px-3 py-1 text-sm font-medium text-gray-600 transition-colors hover:border-(--accent) hover:text-(--accent) dark:border-gray-700 dark:text-gray-400"
          >
            + Add another medication
          </button>
        </div>

        {isEditing ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">Taken at</span>
            <input
              type="date"
              value={editDate}
              min={daysAgoStr(7)}
              max={todayStr()}
              onChange={(e) => setEditDate(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
            />
            <input
              type="time"
              value={editTime}
              onChange={(e) => setEditTime(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
            />
            <button
              onClick={() => applyEdit(group)}
              className="rounded-md bg-(--accent) px-2 py-1 text-xs font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
            >
              Set
            </button>
            <button
              onClick={() => setEditingGroupKey(null)}
              className={`${EDIT_UNDO_CLASS} text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800`}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex-1 text-xs text-gray-500 dark:text-gray-400">
              {untouched
                ? `Expected ${joinDistinct(groupSlots.map((s) => s.anchorLocalTime), to12Hour)}` +
                  (groupExtras.length > 0 ? ` · added at ${joinDistinct(extraTimes, isoTo12Hour)}` : "")
                : anyTakenTime
                  ? `Taken at ${joinDistinct([...takenTimes, ...extraTimes], isoTo12Hour)}`
                  : "Marked as not taken"}
            </span>
            {untouched ? (
              <div className="flex shrink-0 flex-wrap gap-1.5">
                {changeTimeButton}
                <button onClick={() => stageGroupTook(groupSlots)} className={CHOICE_CLASS}>
                  {total > 1 ? "Took all" : "Took it"}
                </button>
                <button onClick={() => stageGroupMissed(groupSlots)} className={CHOICE_CLASS}>
                  {total > 1 ? "Didn't take any" : "Didn't take"}
                </button>
              </div>
            ) : (
              <>
                {changeTimeButton}
                {total > 1 && takenCount < total && (
                  <button
                    onClick={() => stageGroupTook(groupSlots)}
                    className={`${EDIT_UNDO_CLASS} text-(--accent) hover:bg-(--accent)/10`}
                  >
                    All taken
                  </button>
                )}
                <button
                  onClick={() => unstageGroup(group)}
                  className={`${EDIT_UNDO_CLASS} text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800`}
                >
                  Undo
                </button>
              </>
            )}
          </div>
        )}
      </li>
    );
  }

  let stagedTaken = 0;
  let stagedMissed = 0;
  for (const a of staged.values()) {
    if (a.missed) stagedMissed++;
    else stagedTaken++;
  }
  stagedTaken += extras.length;
  const stagedTotal = staged.size + extras.length;

  const modal = (
    <ModalShell
      title="Catch up on missed doses"
      icon={<PillIcon size={18} className="text-(--accent)" />}
      onClose={onClose}
      // The medication picker opens over this modal; Escape should close only the picker.
      escapeEnabled={!pickerGroup}
      footer={
        !nothingLeft && (
          <>
            {stagedTotal > 0 && (
              <p className="mb-2 flex justify-center gap-3 text-xs">
                <span className="text-green-700 dark:text-green-400">✓ {stagedTaken} taken</span>
                <span className="text-red-700 dark:text-red-400">✕ {stagedMissed} not taken</span>
              </p>
            )}
            <button
              onClick={() => void save()}
              disabled={busy || stagedTotal === 0}
              className="w-full rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
            >
              {busy ? "Saving…" : `Save ${stagedTotal} ${stagedTotal === 1 ? "entry" : "entries"}`}
            </button>
          </>
        )
      }
    >
      {error && (
        <p className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </p>
      )}

      {[...skipReasons.entries()].map(([slotId, { name, reason }]) => (
        <p
          key={slotId}
          className="mb-2 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
        >
          {name}: {reason}
        </p>
      ))}

      {nothingLeft ? (
        <p className="py-6 text-center text-sm text-gray-600 dark:text-gray-300">All caught up ✓</p>
      ) : (
        [...byDay.entries()].map(([dayKey, daySlots]) => (
          <div key={dayKey} className="mb-4">
            <p className="mb-1 px-1 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {dayHeading(dayKey)}
            </p>
            <ul className="space-y-2">{groupDaySlots(dayKey, daySlots).map((group) => renderGroup(group))}</ul>
          </div>
        ))
      )}

      {/* Catch-up feedback */}
      {!feedbackOpen ? (
        <div className="mt-2 flex items-center gap-3 border-t border-gray-100 dark:border-gray-800 pt-2">
          <span className="text-xs text-gray-400 dark:text-gray-500">Was this catch-up list accurate?</span>
          <button
            onClick={() => void handleFeedbackThumb("up")}
            disabled={feedbackBusy}
            className={
              "rounded p-1 transition-colors disabled:opacity-40 " +
              (feedbackRating === "up"
                ? "text-green-600 dark:text-green-400"
                : "text-gray-400 hover:text-green-600 dark:text-gray-500 dark:hover:text-green-400")
            }
            title="Thumbs up"
            aria-label="Thumbs up"
          >
            <ThumbUpIcon size={15} />
          </button>
          <button
            onClick={() => void handleFeedbackThumb("down")}
            disabled={feedbackBusy}
            className={
              "rounded p-1 transition-colors disabled:opacity-40 " +
              (feedbackRating === "down"
                ? "text-red-600 dark:text-red-400"
                : "text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400")
            }
            title="Thumbs down"
            aria-label="Thumbs down"
          >
            <ThumbDownIcon size={15} />
          </button>
          {feedbackRating === "up" && (
            <span className="text-xs text-gray-400 dark:text-gray-500">Thanks for the feedback!</span>
          )}
          {feedbackRating === "down" && (
            <button
              onClick={() => setFeedbackOpen(true)}
              className="text-xs text-gray-400 hover:underline dark:text-gray-500"
            >
              Edit reason
            </button>
          )}
          {feedbackError && (
            <span className="text-xs text-red-600 dark:text-red-400">{feedbackError}</span>
          )}
        </div>
      ) : (
        <div className="mt-2 space-y-2 border-t border-gray-100 dark:border-gray-800 pt-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">What went wrong?</span>
            <button
              onClick={() => setFeedbackOpen(false)}
              className="ml-auto text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500"
              aria-label="Close feedback form"
            >
              ✕
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FEEDBACK_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setFeedbackCategory(cat.id)}
                className={
                  "rounded-full border px-2.5 py-1 text-xs transition-colors " +
                  (feedbackCategory === cat.id
                    ? "border-red-400 bg-red-50 text-red-700 dark:border-red-600 dark:bg-red-950/40 dark:text-red-300"
                    : "border-gray-300 text-gray-600 hover:border-gray-400 dark:border-gray-700 dark:text-gray-400")
                }
              >
                {cat.label}
              </button>
            ))}
          </div>
          <ClearableTextarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Tell us what happened…"
            rows={2}
            className="w-full resize-none rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 text-xs text-gray-900 dark:text-gray-100 outline-none focus:ring-1 focus:ring-red-400"
          />
          {feedbackError && (
            <p className="text-xs text-red-600 dark:text-red-400">{feedbackError}</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => void handleFeedbackSubmitDown()}
              disabled={feedbackBusy || !feedbackCategory || !feedbackText.trim()}
              className="rounded bg-red-600 px-3 py-1 text-xs text-white hover:bg-red-700 disabled:opacity-50"
            >
              {feedbackBusy ? "Sending…" : "Submit"}
            </button>
            <button
              onClick={() => setFeedbackOpen(false)}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );

  // Rendered beside (not inside) the modal: ModalShell's dialog is transformed and
  // overflow-hidden, which would make a nested `fixed` overlay position and clip against it.
  return (
    <>
      {modal}
      {pickerGroup && (
        <MedPickerModal
          patientId={patientId}
          title="Add another medication"
          // Already in this group (listed or added), so offering it again is pointless.
          excludeNames={
            new Set([
              ...pickerGroup.slots.map((s) => medKey(s.name)),
              ...extras.filter((x) => x.groupKey === pickerGroup.key).map((x) => medKey(x.choice.name)),
            ])
          }
          // Past-dated doses: the log endpoint skips overdose gates (D-BF-8), so don't flag them here either.
          includeOverdose={false}
          onSelect={(meds) => addExtras(pickerGroup, meds)}
          onClose={() => setPickerGroup(null)}
          onError={setError}
        />
      )}
    </>
  );
}
